import Grid from '@material-ui/core/Grid';
import { isAfter, startOfDay } from 'date-fns';
import { ReactElement, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';

import Button from '~/ui/components/common/Button';
import Checkbox from '~/ui/components/inputs/Checkbox';
import DatePicker from '~/ui/components/inputs/DatePicker';
import Input from '~/ui/components/inputs/Input';
import MedicationSelect from '../../MedicationSelect';

import { useApiMutation } from '~/hooks/api';
import { IAvailableDictionary } from '~/services/api/dictionaries/types';
import { useStoreActions } from '~/store/hooks';
import useMedicationOptions from '../../../hooks/useMedicationOptions';
import { medicationValidationSchema } from './validates';

import { IDetails } from '../types';
import { IMedicationInitialValues, IRequestInfo } from './types';
import { setNoon } from '~/utils/date/date';

import styles from './Styles.module.scss';

interface IProps {
  showSecondSubmit: boolean;
  setModalTitle: (v: any) => void;
  setDetails: (v: any) => void;
  defaultValues: IMedicationInitialValues;
  requestInfo: IRequestInfo;
  details: IDetails | null;
}

const AddMedication = ({
  showSecondSubmit,
  setDetails,
  setModalTitle,
  defaultValues,
  details,
  requestInfo,
}: IProps): ReactElement => {
  const {
    control,
    formState: { errors },
    reset,
    register,
    handleSubmit,
    watch,
  } = useForm({
    defaultValues: {
      ...((defaultValues || {}) as IMedicationInitialValues),
      startDate: defaultValues?.startDate || new Date().toISOString(),
    },
    resolver: medicationValidationSchema,
  });

  const setCurrent = useStoreActions(actions => actions.clientDetails.setCurrent);
  const { onUpdateMedication, onCreateMedication } = useStoreActions(
    actions => actions.clientDetails,
  );
  const { startDate, endDate, id: currentMedicationId } = watch();

  const isDateRangeInvalid = !!(
    startDate &&
    endDate &&
    isAfter(startOfDay(new Date(startDate)), startOfDay(new Date(endDate)))
  );

  const { loading, loadMore, items: medicationOptions, total } = useMedicationOptions();
  const hasFoundMedication = useRef(false);
  const prevOptionsLength = useRef(0);

  const { mutateAsync: updateMedication, isLoading: isUpdating } = useApiMutation(
    onUpdateMedication,
    {
      notifyMessage: 'Medication successfully updated',
      onSuccess: () => {
        setDetails(null);
        setCurrent(null);
      },
    },
  );

  const { mutateAsync: createMedication, isLoading: isCreating } = useApiMutation(
    onCreateMedication,
    {
      notifyMessage: 'Medication successfully added',
      onSuccess: () => {
        reset({ id: null, dose: '', note: '', injectable: false });
      },
    },
  );

  // Auto-load more medication options until current medication is found
  useEffect(() => {
    // Reset the flag when currentMedicationId changes
    hasFoundMedication.current = false;
    prevOptionsLength.current = 0;
  }, [currentMedicationId]);

  useEffect(() => {
    // Effect in order to load more medication options until current medication is found
    if (
      currentMedicationId &&
      loadMore &&
      !loading &&
      !hasFoundMedication.current &&
      medicationOptions.length
    ) {
      const currentMedicationFound = medicationOptions.some(
        (medication: IAvailableDictionary) => medication.id === currentMedicationId,
      );

      if (currentMedicationFound) {
        hasFoundMedication.current = true;
      } else if (
        medicationOptions.length < total &&
        medicationOptions.length > prevOptionsLength.current
      ) {
        prevOptionsLength.current = medicationOptions.length;
        loadMore();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMedicationId, loadMore, loading, total]);

  const onSubmit = async (values: IMedicationInitialValues, evt: any) => {
    const { name } = evt.nativeEvent.submitter;
    if (details && details.id) {
      const payload = {
        requestInfo: { ...requestInfo, id: String(details.id) },
        requestPayload: {
          ...values,
          endDate: setNoon(values.endDate),
          startDate: setNoon(values.startDate),
        },
      };
      await updateMedication(payload);
    } else {
      const payload = {
        requestInfo,
        requestPayload: {
          ...values,
          endDate: setNoon(values.endDate),
          startDate: setNoon(values.startDate),
        },
      };
      await createMedication(payload);
    }
    setModalTitle(name);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={2}>
        <Grid item sm={12}>
          <MedicationSelect
            loading={loading}
            control={control}
            errors={errors}
            isDisabled={!!defaultValues?.id}
            loadMore={loadMore}
          />
        </Grid>
        <Grid item sm={12}>
          <Input
            name="dose"
            register={register}
            label="Medication Dose"
            multiline
            errors={errors}
          />
        </Grid>
        <Grid item sm={12}>
          <Input
            name="note"
            register={register}
            label="Medication Note"
            multiline
            errors={errors}
            minRows={3}
          />
        </Grid>
        <Grid item sm={6}>
          <Checkbox
            size="small"
            name="injectable"
            control={control}
            errors={errors}
            label="Injectable"
          />
        </Grid>
        <Grid item sm={6}>
          <Checkbox
            size="small"
            name="outsideFacility"
            control={control}
            errors={errors}
            label="Outside Facility"
          />
        </Grid>
        <Grid item sm={12}>
          <Grid container spacing={2}>
            <Grid item sm={6}>
              <DatePicker
                name="startDate"
                openTo="date"
                control={control}
                errors={errors}
                label="Start Date"
                maxDate={endDate || undefined}
              />
            </Grid>
            <Grid item sm={6}>
              <DatePicker
                name="endDate"
                openTo="date"
                control={control}
                errors={errors}
                label="End Date"
                minDate={startDate || new Date().toISOString()}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <div className={styles.buttonsWrapper}>
        <Button
          color="primary"
          variant="outlined"
          onClick={() => {
            setDetails(null);
            setModalTitle(null);
            setCurrent(null);
          }}
        >
          Cancel
        </Button>
        {showSecondSubmit && (
          <Button
            color="primary"
            variant="outlined"
            type="submit"
            name="Add Medication"
            className={styles.margin}
            disabled={isDateRangeInvalid || isCreating || isUpdating}
          >
            Save and Add another
          </Button>
        )}
        <Button
          color="primary"
          variant="contained"
          type="submit"
          name={null}
          className={!showSecondSubmit ? styles.margin : ''}
          disabled={isDateRangeInvalid || isCreating || isUpdating}
        >
          {showSecondSubmit ? 'Save' : 'Update'}
        </Button>
      </div>
    </form>
  );
};

export default AddMedication;
